import { Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { times } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getComparator, Order } from '../../../utils/tableHelpers';

export interface Data {
  id: string;
  shortName: string;
  fullName: string;
  taxOffice: string;
  taxNumber: string;
  type: string;
  industry: string;
  authorizedPersonName: string;
  authorizedPersonEmail: string;
  accountManagerId: string;
  manager: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'id',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_ID',
  },
  {
    id: 'shortName',
    numeric: false,
    disablePadding: true,
    label: 'CUSTOMERS.CUSTOMER_SHORT_NAME',
  },
  {
    id: 'industry',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_INDUSTRY',
  },
  {
    id: 'type',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_TYPE',
  },
  {
    id: 'authorizedPersonName',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_AUTHORIZED_PERSON_NAME',
  },
  {
    id: 'authorizedPersonEmail',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_AUTHORIZED_PERSON_EMAIL',
  },
  {
    id: 'accountManagerId',
    numeric: false,
    disablePadding: false,
    label: 'CUSTOMERS.CUSTOMER_ACCOUNT_MANAGER',
  },
];

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Order;
  orderBy: string;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const { t } = useTranslation();

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {t(headCell.label)}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function CustomersTable({
  rows,
  setSelectedCustomer,
  isLoading,
}: {
  rows: Data[];
  setSelectedCustomer: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
}) {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('shortName');
  const [selected, setSelected] = React.useState<string>('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const { t } = useTranslation();
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    id: string,
    name: string
  ) => {
    setSelected((prevSelected) => {
      return prevSelected !== id ? id : '';
    });
    const row = rows.find((row) => row.id === id);
    if (row) {
      setSelectedCustomer(row.id);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const Row = ({
    row,
    isItemSelected,
    labelId,
  }: {
    row: Data;
    isItemSelected: boolean;
    labelId: string;
  }) => {
    return (
      <>
        <TableRow
          hover
          onClick={(event) => handleClick(event, row.id, row.shortName)}
          role="checkbox"
          aria-checked={isItemSelected}
          tabIndex={-1}
          key={row.id}
          data-testid={row.id}
          selected={isItemSelected}
          sx={{ cursor: 'pointer' }}
        >
          <TableCell component="th" id={labelId} scope="row">
            {row.id}
          </TableCell>
          <TableCell align="left">{row.shortName}</TableCell>
          <TableCell align="left">
            {row.industry ? t(`INDUSTRIES.${row.industry}`) : ''}
          </TableCell>
          <TableCell align="left">
            {row.type ? t(`CUSTOMERS.CUSTOMER_TYPE.${row.type}`) : ''}
          </TableCell>
          <TableCell align="left">{row.authorizedPersonName}</TableCell>
          <TableCell align="left">{row.authorizedPersonEmail}</TableCell>
          <TableCell align="left">
            {row.manager.id
              ? `${row.manager.firstName} ${row.manager.lastName}`
              : ''}
          </TableCell>
        </TableRow>
      </>
    );
  };

  const isSelected = ($id: string) => selected.indexOf($id) !== -1;

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  let rowDisplay;
  if (isLoading) {
    rowDisplay = times(3, (index) => {
      return (
        <TableRow key={index}>
          {times(7, (index) => (
            <TableCell align="left" key={index}>
              <Skeleton animation="wave" />
            </TableCell>
          ))}
        </TableRow>
      );
    });
  } else {
    rowDisplay = rows
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row, index) => {
        const isItemSelected = isSelected(row.id);
        const labelId = `enhanced-table-checkbox-${index}`;
        return (
          <Row
            key={row.id}
            row={row}
            isItemSelected={isItemSelected}
            labelId={labelId}
          />
        );
      });
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="customersTable">
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rowDisplay}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 53 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          data-testid="rowsPerPage"
        />
      </Paper>
    </Box>
  );
}
